import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Card } from '../../../components/Shared/Card';
import { Button } from '../../../components/Shared/Button';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from '../../../components/Shared/Table';
import { formatDate } from '@/utils/dateUtils';
import { formatMoney } from '@/utils/money';
import { formatPercent } from '@/utils/percent';
import { invoiceUnitKey } from '@/utils/invoiceUnit';
import { billingKindLabelKey, needsReview } from '@/utils/billingKind';
import type { FacilityFlowCoreModulesInvoicesDtosInvoicePositionDto } from '../../../api/generated/models';

type InvoicePositionDto = FacilityFlowCoreModulesInvoicesDtosInvoicePositionDto;

interface InvoicePositionsSectionProps {
  /** Already sorted by displayOrder — indices passed to onMove refer to this order. */
  positions: InvoicePositionDto[];
  /** Only draft invoices expose add / edit / delete / move (ADR-0018, Api repo). */
  isEditable: boolean;
  saving: boolean;
  onAdd: () => void;
  onEdit: (position: InvoicePositionDto) => void;
  onDelete: (position: InvoicePositionDto) => void;
  onMove: (index: number, direction: 'up' | 'down') => void;
}

export function InvoicePositionsSection({
  positions, isEditable, saving, onAdd, onEdit, onDelete, onMove,
}: InvoicePositionsSectionProps) {
  const { t } = useTranslation(['invoices', 'common', 'offers']);

  return (
    <Card
      title={t('invoices:positions.title')}
      actions={
        isEditable ? (
          <Button
            size="sm"
            onClick={onAdd}
            disabled={saving}
            leftIcon={<Plus className="w-3.5 h-3.5" strokeWidth={2} />}
          >
            {t('invoices:positions.add')}
          </Button>
        ) : undefined
      }
      noPadding={positions.length > 0}
    >
      {positions.length === 0 ? (
        <EmptyState
          title={isEditable
            ? t('invoices:positions.emptyEditable')
            : t('invoices:positions.empty')}
        />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-12 text-right">
                {t('invoices:positions.table.pos')}
              </TableHeader>
              <TableHeader>
                {t('invoices:positions.table.title')}
              </TableHeader>
              <TableHeader className="hidden md:table-cell">
                {t('invoices:positions.table.source')}
              </TableHeader>
              <TableHeader className="text-right">
                {t('invoices:positions.table.quantity')}
              </TableHeader>
              <TableHeader className="hidden sm:table-cell">
                {t('invoices:positions.table.unit')}
              </TableHeader>
              <TableHeader className="text-right">
                {t('invoices:positions.table.unitPrice')}
              </TableHeader>
              <TableHeader className="hidden sm:table-cell text-right">
                {t('invoices:positions.table.vatRate')}
              </TableHeader>
              <TableHeader className="text-right">
                {t('invoices:positions.table.net')}
              </TableHeader>
              {isEditable && (
                <TableHeader className="w-36 text-right">
                  {t('invoices:table.actions')}
                </TableHeader>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((p, idx) => {
              const source = (p.sourceType ?? '').toLowerCase();
              let sourceLabel = t('invoices:positionSource.manual');
              let sourceVariant: 'default' | 'info' | 'success' = 'default';
              if (source === 'recurring') {
                // why: from the offer, in its own rhythm — older lines carry no kind (FAFD-27).
                sourceLabel = needsReview(p.billingKind)
                  ? t('invoices:positionSource.recurring')
                  : `${t('invoices:positionSource.recurring')} · ${t(billingKindLabelKey(p.billingKind))}`;
                sourceVariant = 'info';
              } else if (source === 'adhoc') {
                sourceLabel = t('invoices:positionSource.adhoc');
                sourceVariant = 'success';
              }
              return (
                <TableRow key={p.id}>
                  <TableCell className="text-right tabular-nums">
                    {p.positionNumber}
                  </TableCell>
                  <TableCell>
                    <div className="text-[13px] font-medium text-slate-900">
                      {p.title || '—'}
                    </div>
                    {p.description && (
                      <div className="text-[12px] text-slate-500 mt-0.5">
                        {p.description}
                      </div>
                    )}
                    {(p.serviceDateFrom || p.serviceDateTo) && (
                      <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
                        {p.serviceDateTo && p.serviceDateTo !== p.serviceDateFrom
                          ? t('invoices:positions.serviceDateRange', {
                              from: formatDate(p.serviceDateFrom ?? undefined),
                              to: formatDate(p.serviceDateTo),
                            })
                          : t('invoices:positions.serviceDate', {
                              from: formatDate(p.serviceDateFrom ?? undefined),
                            })}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant={sourceVariant}>{sourceLabel}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {(p.quantity ?? 0).toLocaleString('de-DE')}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {(() => {
                      const key = invoiceUnitKey(p.unit);
                      return key ? t(key, { count: p.quantity ?? 0 }) : p.unit || '—';
                    })()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMoney(p.unitNetPrice ?? 0)}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-right tabular-nums">
                    {formatPercent(p.vatRate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(p.netAmount ?? 0)}
                  </TableCell>
                  {isEditable && (
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost" size="sm"
                          title={t('common:actions.moveUp')}
                          aria-label={t('common:actions.moveUp')}
                          className="p-1.5"
                          disabled={saving || idx === 0}
                          onClick={() => onMove(idx, 'up')}
                        >
                          <ChevronUp className="w-4 h-4" strokeWidth={2} />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          title={t('common:actions.moveDown')}
                          aria-label={t('common:actions.moveDown')}
                          className="p-1.5"
                          disabled={saving || idx === positions.length - 1}
                          onClick={() => onMove(idx, 'down')}
                        >
                          <ChevronDown className="w-4 h-4" strokeWidth={2} />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          title={t('invoices:positions.edit')}
                          aria-label={t('invoices:positions.edit')}
                          className="p-1.5"
                          disabled={saving}
                          onClick={() => onEdit(p)}
                        >
                          <Pencil className="w-4 h-4" strokeWidth={2} />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          title={t('invoices:positions.delete')}
                          aria-label={t('invoices:positions.delete')}
                          className="p-1.5"
                          disabled={saving}
                          onClick={() => onDelete(p)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

export default InvoicePositionsSection;
